import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FileText, Save, Loader2, RotateCcw, Eye, EyeOff } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import SafeHtml from './SafeHtml';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const KNOWN_TEXTS = [
  {
    key: 'ticket_response_estimate',
    label: 'Ticket — engineer response estimate',
    hint: 'Shown above the chat in every ticket. Tells the customer how soon they can expect an answer. Supports rich formatting & colors.',
  },
  {
    key: 'support_ticket_time_message',
    label: 'Support ticket time message (1-year warranty lock)',
    hint: 'Shown to the customer (only) when the order is older than 1 year. Use the placeholder {created_date} to insert the order creation date. Admins can always reply regardless of order age.',
  },
  {
    key: 'ecu_finder_hint',
    label: 'New Order — ECU finder hint',
    hint: 'Inline hint shown under the Model dropdown on the customer New Order wizard. Used to nudge customers to the "Find by ECU" fallback when their model is not in the catalog. Plain text or simple inline formatting. Leave empty to fall back to the built-in default.',
  },
];

// Quill toolbar with text/bg color, bold, italic, underline, headers, lists, link
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'clean'],
  ],
};

const QUILL_FORMATS = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet',
  'align',
  'link',
];

// Strip HTML to count plain text length (for the 5000-char limit display).
// Uses DOMParser (doesn't execute scripts or load resources) instead of
// the element-innerHTML trick — keeps the security scanner clean.
const stripHtml = (html) => {
  if (!html) return '';
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    return (doc.body.textContent || '').trim();
  } catch {
    return String(html).replace(/<[^>]*>/g, '').trim();
  }
};

export default function ConfigTextsEditor({ token }) {
  const [texts, setTexts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [previews, setPreviews] = useState({}); // {key: bool}

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/config/texts`);
      if (!r.ok) return;
      setTexts((await r.json()) || {});
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/config/texts`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(texts),
      });
      if (!r.ok) { alert('Save failed: ' + await r.text()); return; }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      load();
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center text-slate-600">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading texts…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="config-texts-editor">
      <div className="flex items-start gap-3 p-4 bg-cyan-50 border border-cyan-200 rounded-xl">
        <FileText className="w-5 h-5 text-cyan-700 mt-0.5" />
        <div className="text-sm text-cyan-900">
          <p className="font-semibold mb-1">UI Text Snippets — Rich Editor</p>
          <p className="text-cyan-800">
            Edit the customer-facing copy with full formatting: <b>bold</b>,
            <span style={{ color: '#dc2626' }}> colors</span>, lists, links, etc.
            Changes go live immediately for every customer.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">
        {KNOWN_TEXTS.map((t) => {
          const value = texts[t.key] || '';
          const plainLen = stripHtml(value).length;
          const previewing = !!previews[t.key];
          return (
            <div key={t.key} className="p-5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <label className="text-sm font-bold text-slate-800 block">{t.label}</label>
                  <p className="text-xs text-slate-500 mt-0.5">{t.hint}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setPreviews((p) => ({ ...p, [t.key]: !p[t.key] }))}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium"
                  data-testid={`toggle-preview-${t.key}`}
                  title="Toggle live preview"
                >
                  {previewing ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  {previewing ? 'Hide preview' : 'Preview'}
                </button>
              </div>

              {previewing ? (
                <SafeHtml
                  as="div"
                  className="border border-amber-300 rounded-lg bg-gradient-to-r from-amber-50 to-yellow-50 p-3 text-sm text-amber-900 min-h-[120px] prose prose-sm max-w-none"
                  data-testid={`preview-${t.key}`}
                  // Editor output is sanitized by SafeHtml/DOMPurify on render.
                  html={value || '<em style="color:#94a3b8">Empty — nothing will be shown to customers.</em>'}
                />
              ) : (
                <div className="quill-wrapper border border-slate-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-cyan-500" data-testid={`config-text-${t.key}`}>
                  <ReactQuill
                    theme="snow"
                    value={value}
                    onChange={(html) => setTexts((cur) => ({ ...cur, [t.key]: html }))}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    placeholder="Type the message customers will see…"
                  />
                </div>
              )}

              <div className="flex justify-between items-center mt-2">
                <p className="text-[10px] text-slate-400">key: <code>{t.key}</code></p>
                <p className={`text-[10px] ${plainLen > 4500 ? 'text-amber-600 font-semibold' : 'text-slate-400'}`}>
                  {plainLen} chars · {value.length}/5000 stored
                </p>
              </div>
            </div>
          );
        })}
        <div className="p-4 flex gap-2 items-center">
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-config-texts"
            className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-medium disabled:opacity-50 hover:shadow-md flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
          </button>
          <button
            onClick={() => setTexts({})}
            className="px-4 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 flex items-center gap-2"
            data-testid="clear-config-texts"
            title="Empty all to revert to defaults on next load"
          >
            <RotateCcw className="w-4 h-4" /> Clear all
          </button>
          {savedFlash && (
            <span className="self-center text-emerald-600 text-sm font-medium" data-testid="config-texts-saved">
              ✓ Saved
            </span>
          )}
        </div>
      </div>

      {/* Local style overrides so Quill blends with the rest of the admin UI */}
      <style>{`
        .quill-wrapper .ql-toolbar { border: 0; border-bottom: 1px solid #e2e8f0; background: #f8fafc; }
        .quill-wrapper .ql-container { border: 0; min-height: 140px; font-size: 14px; }
        .quill-wrapper .ql-editor { min-height: 140px; }
      `}</style>
    </div>
  );
}
